import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  FitnessCenter as FitnessCenterIcon,
  Timer as TimerIcon,
  LocalFireDepartment as CaloriesIcon
} from '@mui/icons-material';
import api from '../utils/api';

const WorkoutLogForm = ({ open, onClose, onSave, editingWorkout = null }) => {
  
  const [formData, setFormData] = useState({
    title: '',
    type: 'strength',
    duration: '',
    intensity: 'moderate',
    caloriesBurned: '',
    facility: '',
    trainer: '',
    notes: '',
    feeling: 'good',
    tags: [],
    isPublic: false,
    exercises: []
  });

  const [facilities, setFacilities] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [currentTag, setCurrentTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchOptions();
      if (editingWorkout) {
        setFormData({
          ...editingWorkout,
          facility: editingWorkout.facility?._id || '',
          trainer: editingWorkout.trainer?._id || '',
          tags: editingWorkout.tags || []
        });
      } else {
        resetForm();
      }
    }
  }, [open, editingWorkout]);

  const fetchOptions = async () => {
    try {
      const [facilitiesRes, trainersRes] = await Promise.all([
        api.get('/facilities/all'),
        api.get('/users?role=trainer')
      ]);
      
      setFacilities(facilitiesRes.data.data || []);
      setTrainers(trainersRes.data.data || []);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      type: 'strength',
      duration: '',
      intensity: 'moderate',
      caloriesBurned: '',
      facility: '',
      trainer: '',
      notes: '',
      feeling: 'good',
      tags: [],
      isPublic: false,
      exercises: []
    });
    setCurrentTag('');
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addExercise = () => {
    setFormData(prev => ({
      ...prev,
      exercises: [
        ...prev.exercises,
        {
          name: '',
          category: 'strength',
          sets: [{ reps: '', weight: '', duration: '', distance: '', restTime: '', notes: '' }],
          notes: ''
        }
      ]
    }));
  };

  const removeExercise = (index) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  const updateExercise = (exerciseIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.map((exercise, i) => 
        i === exerciseIndex ? { ...exercise, [field]: value } : exercise
      )
    }));
  };

  const addSet = (exerciseIndex) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.map((exercise, i) => 
        i === exerciseIndex 
          ? { 
              ...exercise, 
              sets: [...exercise.sets, { reps: '', weight: '', duration: '', distance: '', restTime: '', notes: '' }]
            }
          : exercise
      )
    }));
  };

  const removeSet = (exerciseIndex, setIndex) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.map((exercise, i) => 
        i === exerciseIndex 
          ? { 
              ...exercise, 
              sets: exercise.sets.filter((_, j) => j !== setIndex)
            }
          : exercise
      )
    }));
  };

  const updateSet = (exerciseIndex, setIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.map((exercise, i) => 
        i === exerciseIndex 
          ? {
              ...exercise,
              sets: exercise.sets.map((set, j) => 
                j === setIndex ? { ...set, [field]: value } : set
              )
            }
          : exercise
      )
    }));
  };

  const addTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validation
      if (!formData.title.trim()) {
        setError('Workout title is required');
        return;
      }
      if (!formData.duration || formData.duration <= 0) {
        setError('Duration must be greater than 0');
        return;
      }

      const workoutData = {
        ...formData,
        duration: parseInt(formData.duration),
        caloriesBurned: formData.caloriesBurned ? parseInt(formData.caloriesBurned) : undefined,
        facility: formData.facility || undefined,
        trainer: formData.trainer || undefined
      };

      if (editingWorkout) {
        await api.put(`/workout-logs/${editingWorkout._id}`, workoutData);
      } else {
        await api.post('/workout-logs', workoutData);
      }

      onSave();
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save workout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <FitnessCenterIcon color="primary" />
          <Typography variant="h6">
            {editingWorkout ? 'Edit Workout' : 'Log New Workout'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Workout Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              required
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <MenuItem value="strength">Strength</MenuItem>
                <MenuItem value="cardio">Cardio</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
                <MenuItem value="sports">Sports</MenuItem>
                <MenuItem value="flexibility">Flexibility</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Intensity</InputLabel>
              <Select
                value={formData.intensity}
                label="Intensity"
                onChange={(e) => handleInputChange('intensity', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="moderate">Moderate</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="very_high">Very High</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={formData.duration}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              InputProps={{
                startAdornment: <TimerIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              required
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Calories Burned"
              type="number"
              value={formData.caloriesBurned}
              onChange={(e) => handleInputChange('caloriesBurned', e.target.value)}
              InputProps={{
                startAdornment: <CaloriesIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select
                value={formData.facility}
                label="Facility"
                onChange={(e) => handleInputChange('facility', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {facilities.map(facility => (
                  <MenuItem key={facility._id} value={facility._id}>
                    {facility.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Trainer</InputLabel>
              <Select
                value={formData.trainer}
                label="Trainer"
                onChange={(e) => handleInputChange('trainer', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {trainers.map(trainer => (
                  <MenuItem key={trainer._id} value={trainer._id}>
                    {trainer.firstName} {trainer.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>How did you feel?</InputLabel>
              <Select
                value={formData.feeling}
                label="How did you feel?"
                onChange={(e) => handleInputChange('feeling', e.target.value)}
              >
                <MenuItem value="terrible">😰 Terrible</MenuItem>
                <MenuItem value="bad">😔 Bad</MenuItem>
                <MenuItem value="okay">😐 Okay</MenuItem>
                <MenuItem value="good">😊 Good</MenuItem>
                <MenuItem value="great">😍 Great</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Tags */}
          <Grid size={12}>
            <Typography variant="subtitle1" gutterBottom>
              Tags
            </Typography>
            <Box display="flex" gap={1} alignItems="center" mb={2}>
              <TextField
                size="small"
                label="Add tag"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
              />
              <Button variant="outlined" onClick={addTag} disabled={!currentTag.trim()}>
                Add
              </Button>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => removeTag(tag)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Grid>

          {/* Exercises */}
          <Grid size={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Exercises
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addExercise}
              >
                Add Exercise
              </Button>
            </Box>

            {formData.exercises.map((exercise, exerciseIndex) => (
              <Box key={exerciseIndex} sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Exercise {exerciseIndex + 1}
                  </Typography>
                  <IconButton 
                    color="error" 
                    onClick={() => removeExercise(exerciseIndex)}
                    size="small"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Exercise Name"
                      value={exercise.name}
                      onChange={(e) => updateExercise(exerciseIndex, 'name', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={exercise.category}
                        label="Category"
                        onChange={(e) => updateExercise(exerciseIndex, 'category', e.target.value)}
                      >
                        <MenuItem value="strength">Strength</MenuItem>
                        <MenuItem value="cardio">Cardio</MenuItem>
                        <MenuItem value="flexibility">Flexibility</MenuItem>
                        <MenuItem value="balance">Balance</MenuItem>
                        <MenuItem value="sports">Sports</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Sets */}
                <Typography variant="subtitle2" gutterBottom>
                  Sets
                </Typography>
                {exercise.sets.map((set, setIndex) => (
                  <Box key={setIndex} sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" fontWeight={600}>
                        Set {setIndex + 1}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => removeSet(exerciseIndex, setIndex)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Reps"
                          type="number"
                          value={set.reps}
                          onChange={(e) => updateSet(exerciseIndex, setIndex, 'reps', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Weight (kg)"
                          type="number"
                          value={set.weight}
                          onChange={(e) => updateSet(exerciseIndex, setIndex, 'weight', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Duration (sec)"
                          type="number"
                          value={set.duration}
                          onChange={(e) => updateSet(exerciseIndex, setIndex, 'duration', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Distance (m)"
                          type="number"
                          value={set.distance}
                          onChange={(e) => updateSet(exerciseIndex, setIndex, 'distance', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Rest (sec)"
                          type="number"
                          value={set.restTime}
                          onChange={(e) => updateSet(exerciseIndex, setIndex, 'restTime', e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Notes"
                          value={set.notes}
                          onChange={(e) => updateSet(exerciseIndex, setIndex, 'notes', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addSet(exerciseIndex)}
                >
                  Add Set
                </Button>

                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Exercise Notes"
                  value={exercise.notes}
                  onChange={(e) => updateExercise(exerciseIndex, 'notes', e.target.value)}
                  sx={{ mt: 2 }}
                />
              </Box>
            ))}
          </Grid>

          {/* General Notes */}
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Workout Notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : (editingWorkout ? 'Update Workout' : 'Save Workout')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkoutLogForm;